import type { IFacilityItem } from '../Infrastructure/Interfaces';

export type ExtractedFacilityData = {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const ABBREVIATIONS: Record<string, string> = {
  st: 'saint',
  sts: 'saints',
  med: 'medical',
  ctr: 'center',
  cntrl: 'central',
  hosp: 'hospital',
  hlth: 'health',
  hlthcr: 'healthcare',
  mem: 'memorial',
  reg: 'regional',
  rgnl: 'regional',
  cmty: 'community',
  comm: 'community',
  gen: 'general',
  univ: 'university',
  dept: 'department',
  svc: 'service',
  svcs: 'services',
  mtn: 'mountain',
  mts: 'mountains',
};

const normalizeTokens = (value: string | null | undefined): string[] => (
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => ABBREVIATIONS[word] || word)
    .filter(Boolean)
);

const tokenSimilarity = (left: string | null | undefined, right: string | null | undefined): number => {
  const leftTokens = new Set(normalizeTokens(left));
  const rightTokens = new Set(normalizeTokens(right));
  if (leftTokens.size === 0 || rightTokens.size === 0) return 0;

  let matches = 0;
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) matches += 1;
  });
  return (2 * matches) / (leftTokens.size + rightTokens.size);
};

const exactSimilarity = (left: string | null | undefined, right: string | null | undefined): number => {
  const normalizedLeft = normalizeTokens(left).join(' ');
  const normalizedRight = normalizeTokens(right).join(' ');
  return normalizedLeft && normalizedLeft === normalizedRight ? 1 : 0;
};

const zipSimilarity = (left: string | null | undefined, right: string | null | undefined): number => {
  const leftZip = (left || '').replace(/\D/g, '').slice(0, 5);
  const rightZip = (right || '').replace(/\D/g, '').slice(0, 5);
  return leftZip.length === 5 && leftZip === rightZip ? 1 : 0;
};

const scoreLocation = (
  extracted: ExtractedFacilityData,
  address: string,
  city: string,
  state: string,
  zip: string,
): number => {
  const suppliedWeights = {
    address: extracted.address ? 0.5 : 0,
    city: extracted.city ? 0.2 : 0,
    state: extracted.state ? 0.1 : 0,
    zip: extracted.zip ? 0.2 : 0,
  };
  const totalWeight = Object.values(suppliedWeights).reduce((sum, weight) => sum + weight, 0);
  if (totalWeight === 0) return 0;

  return (
    tokenSimilarity(extracted.address, address) * suppliedWeights.address +
    exactSimilarity(extracted.city, city) * suppliedWeights.city +
    exactSimilarity(extracted.state, state) * suppliedWeights.state +
    zipSimilarity(extracted.zip, zip) * suppliedWeights.zip
  ) / totalWeight;
};

export function findBestFacilityMatch(
  extracted: ExtractedFacilityData,
  facilities: IFacilityItem[],
): IFacilityItem | undefined {
  if (!extracted.name?.trim() || facilities.length === 0) return undefined;

  let bestMatch: IFacilityItem | undefined;
  let bestScore = 0;
  let bestNameScore = 0;

  facilities.forEach((facility) => {
    const nameScore = Math.max(
      tokenSimilarity(extracted.name, facility.facilityName),
      tokenSimilarity(extracted.name, facility.efrFacilityName),
      tokenSimilarity(extracted.name, facility.availityFacility),
    );
    const locationScore = Math.max(
      scoreLocation(extracted, facility.physicalAddress, facility.physicalCity, facility.physicalState, facility.physicalZip),
      scoreLocation(extracted, facility.lockboxAddress, facility.lockboxCity, facility.lockboxState, facility.lockboxZip),
    );
    const hasExtractedLocation = !!(extracted.address || extracted.city || extracted.state || extracted.zip);
    const score = hasExtractedLocation ? nameScore * 0.65 + locationScore * 0.35 : nameScore;

    if (score > bestScore) {
      bestScore = score;
      bestNameScore = nameScore;
      bestMatch = facility;
    }
  });

  return bestMatch && bestNameScore >= 0.5 && bestScore >= 0.55 ? bestMatch : undefined;
}